import { z } from 'zod';
import { LOCALES, type Locale } from './locales';
import { resolveLocale } from './resolve-locale';

const KEY = 'kit.locale';

class LocaleStore {
  private readonly listeners = new Set<() => void>();

  /** The person's choice, otherwise the browser's language. */
  read = (): Locale => {
    const stored = localStorage.getItem(KEY);
    if (stored && (LOCALES as readonly string[]).includes(stored)) return stored as Locale;
    return resolveLocale(navigator.language);
  };

  chosen(): Locale | null {
    const stored = localStorage.getItem(KEY);
    return stored && (LOCALES as readonly string[]).includes(stored) ? (stored as Locale) : null;
  }

  /** `<html lang>` and zod's built-in validation messages follow the locale. */
  apply(): void {
    const locale = this.read();
    document.documentElement.lang = locale.slice(0, 2);
    z.config(locale === 'es-ES' ? z.locales.es() : z.locales.en());
  }

  /** `null` goes back to the browser's language. */
  set(locale: Locale | null): void {
    if (locale === null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, locale);
    this.apply();
    for (const listener of this.listeners) listener();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
}

export const localeStore = new LocaleStore();
